import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PLATFORM_BILLING_MODELS,
  filterPlatformReport,
  filterPlatforms,
  normalizePlatformFilters,
  platformCategoryOptions,
  platformKindOf,
  platformClientOptions,
  type PlatformFilters,
  platformBillingOverview,
  platformBillingRows,
  platformSpendRange,
  platformRowsToCsv,
  sortPlatformRows,
  type Platform,
  type PlatformReport,
  type PlatformRowSortKey,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, deletePlatform, fetchPlatformReport, fetchPlatforms } from "../services/expensesService";
import { fetchPlatformBilling, type PlatformBillingResponse } from "../services/platformBillingService";
import { Chip, ErrorBox, Kpi, Notice, Page } from "../components/ui";
import PlatformDialog from "../components/PlatformDialog";
import PlatformFiltersBar from "../components/PlatformFiltersBar";
import PlatformKindSummary from "../components/PlatformKindSummary";
import { platformFiltersFromQuery, platformFiltersToQuery } from "../lib/platformFiltersUrl";
import PlatformInvoicesDialog from "../components/PlatformInvoicesDialog";
import PlatformInvoiceHistory from "../components/PlatformInvoiceHistory";
import PlatformSpendCharts from "../components/PlatformSpendCharts";
import { currentDay, currentMonth, downloadText, lang, money, monthLabel } from "../lib/format";
import { GAP_TONES, MODEL_TONES, PLATFORM_KIND_TONES, currencyTotals, percentChange } from "../lib/platformBilling";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";

// Pago de plataformas (#956 / #957): cuántas y cuáles plataformas se pagan,
// cuánto cuesta cada una según su modelo de cobro (asiento, servicio o
// consumo), lo pagado de verdad mes a mes y sus facturas.

const COLUMNS: { key: PlatformRowSortKey; numeric?: boolean }[] = [
  { key: "name" },
  { key: "vendor" },
  { key: "model" },
  { key: "seats", numeric: true },
  { key: "unitPrice", numeric: true },
  { key: "monthlyCost", numeric: true },
  { key: "frequency" },
  { key: "nextRenewal" },
  { key: "card" },
  { key: "owner" },
];

/** Repos que la usan, tenants, licenciamiento y comprobante esperado, bajo el nombre. */
function DiligenceLine({ p }: { p: Platform }) {
  const { t: tp } = useTranslation(PLATFORMS_NS);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const repos = (p.usedBy || []).map((u) => u.repo);
  if (!repos.length && !p.tenants?.length && !p.licensing && !p.invoiceType && !p.paidBy) return null;
  return (
    <div className={`mt-1 flex max-w-[340px] flex-wrap gap-1 text-xs font-normal ${c.muted}`} data-testid={`diligence-${p.id}`}>
      {repos.map((r) => (
        <Chip key={r} className="bg-sky-500/10 text-sky-700 dark:text-sky-300">{r}</Chip>
      ))}
      {p.shared && <Chip className="bg-violet-500/10 text-violet-700 dark:text-violet-300">{tp("diligence.sharedChip")}</Chip>}
      {p.licensing && <Chip className="bg-gray-400/15">{tp(`licensing.${p.licensing}`)}</Chip>}
      {p.invoiceType && <Chip className="bg-gray-400/15">{tp(`invoiceType.${p.invoiceType}`)}</Chip>}
      {p.tenants?.length ? <span title={tp("diligence.tenants")}>{p.tenants.join(" · ")}</span> : null}
      {p.paidBy && <span>{`${tp("diligence.paidBy")}: ${p.paidBy}`}</span>}
    </div>
  );
}

export default function PlatformsSection() {
  const { t, i18n } = useTranslation("common");
  const { t: tp } = useTranslation(PLATFORMS_NS);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const language = i18n.language;
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [report, setReport] = useState<PlatformReport[]>([]);
  const [billing, setBilling] = useState<PlatformBillingResponse | null>(null);
  const [spendReport, setSpendReport] = useState<PlatformReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ open: boolean; platform: Platform | null }>({ open: false, platform: null });
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [history, setHistory] = useState<Platform | null>(null);
  const [sort, setSort] = useState<{ key: PlatformRowSortKey; dir: "asc" | "desc" }>({ key: "monthlyCost", dir: "desc" });
  // Filtros: salen de la URL (?desde=&hasta=&cliente=&tipo=&modelo=&licencia=&estado=) y se escriben de vuelta.
  const [filters, setFilters] = useState<PlatformFilters>(() => platformFiltersFromQuery(window.location.search, currentMonth()));
  const updateFilters = useCallback((next: PlatformFilters) => {
    const month = currentMonth();
    const clean = normalizePlatformFilters(next, month);
    setFilters(clean);
    try {
      const { pathname, search, hash } = window.location;
      window.history.replaceState(window.history.state, "", `${pathname}${platformFiltersToQuery(clean, month, search)}${hash}`);
    } catch {
      // Sin historial (vista previa, iframe): el filtro sigue funcionando sin URL.
    }
  }, []);

  // Lo pagado en el rango elegido: su propia petición, y se descarta una respuesta que llegue tarde.
  const [reportVersion, setReportVersion] = useState(0);
  useEffect(() => {
    let current = true;
    fetchPlatformReport({ from: filters.from, to: filters.to })
      .then((rep) => {
        if (current) setReport(rep.platforms);
      })
      .catch((err) => {
        if (current) setError(apiErrorMessage(err) || t("expenses.errors.load"));
      });
    return () => {
      current = false;
    };
  }, [filters.from, filters.to, reportVersion, t]);

  const load = useCallback(async () => {
    try {
      setReportVersion((v) => v + 1);
      const [list, bill, hist] = await Promise.all([
        fetchPlatforms(),
        fetchPlatformBilling(),
        // Gráficas del encabezado: enero de hace 3 años → hoy.
        fetchPlatformReport(platformSpendRange(currentDay())),
      ]);
      setPlatforms(list);
      setSpendReport(hist.platforms);
      setBilling(bill);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const rates = useMemo(() => billing?.rates || {}, [billing]);
  const today = billing?.today || new Date().toISOString().slice(0, 10);
  // El encabezado se calcula en el cliente con la misma función del servidor: refleja al instante lo editado.
  // Todo lo de abajo (encabezado, tabla, CSV, pagado, consumo y gráficas) trabaja sobre las plataformas filtradas.
  const visible = useMemo(() => filterPlatforms(platforms, filters), [platforms, filters]);
  const visibleIds = useMemo(() => new Set(visible.map((p) => p.id)), [visible]);
  // Plataformas vs. infraestructura: el resumen por tipo ve lo que pasa los demás filtros, sin el de tipo.
  const kindScope = useMemo(() => filterPlatforms(platforms, { ...filters, kind: "" }), [platforms, filters]);
  const kindScopeSpend = useMemo(() => (spendReport ? filterPlatformReport(spendReport, new Set(kindScope.map((p) => p.id))) : null), [spendReport, kindScope]);
  const clients = useMemo(() => platformClientOptions(platforms), [platforms]);
  const categories = useMemo(() => platformCategoryOptions(platforms), [platforms]);
  const overview = useMemo(() => platformBillingOverview(visible, rates), [visible, rates]);
  const rows = useMemo(() => sortPlatformRows(platformBillingRows(visible, today), sort.key, sort.dir, rates), [visible, today, sort, rates]);
  const visibleGaps = useMemo(() => (billing?.gaps || []).filter((g) => visibleIds.has(g.platformId)), [billing, visibleIds]);
  const gapsById = useMemo(() => new Map(visibleGaps.map((g) => [g.platformId, g])), [visibleGaps]);
  const visibleReport = useMemo(() => filterPlatformReport(report, visibleIds), [report, visibleIds]);
  const visibleSpend = useMemo(() => (spendReport ? filterPlatformReport(spendReport, visibleIds) : null), [spendReport, visibleIds]);
  const consumption = useMemo(() => (billing?.consumption || []).filter((r) => visibleIds.has(r.platformId)), [billing, visibleIds]);
  const months = report[0]?.months.map((m) => m.period) || [];
  const missingInvoices = visibleGaps.reduce((s, g) => s + g.missingInvoices, 0);
  const spikes = consumption.flatMap((r) =>
    r.months.slice(-2).filter((m) => m.spike).map((m) => ({ ...m, name: r.name })),
  );

  const toggleSort = (key: PlatformRowSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "monthlyCost" || key === "seats" || key === "unitPrice" ? "desc" : "asc" }));

  const exportCsv = () => downloadText(`pago-de-plataformas-${today}.csv`, platformRowsToCsv(rows, lang(language)));

  const remove = async (p: Platform) => {
    if (!window.confirm(tp("confirmDelete", { name: p.name }))) return;
    try {
      await deletePlatform(p.id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    }
  };

  const monthlyHint = overview.monthlyBase != null
    ? Object.keys(overview.monthlyByCurrency).some((cur) => cur !== "MXN")
      ? tp("kpi.monthlyApprox", { value: money(overview.monthlyBase, "MXN", language) })
      : undefined
    : tp("kpi.monthlyNoRate", { currencies: overview.missingRates.join(", ") });

  return (
    <Page
      title={tp("title")}
      subtitle={tp("subtitle")}
      actions={
        <>
          <button type="button" className={c.secondary} onClick={() => setInvoicesOpen(true)}>
            <i className="ri-file-upload-line mr-1" />
            {tp("uploadInvoices")}
            {billing?.pendingInvoices ? <Chip className="ml-2 bg-amber-500/15 text-amber-600">{billing.pendingInvoices}</Chip> : null}
          </button>
          <button type="button" className={c.secondary} onClick={exportCsv} disabled={!rows.length}>
            <i className="ri-download-2-line mr-1" />
            {tp("exportCsv")}
          </button>
          <button type="button" className={c.primary} onClick={() => setEditing({ open: true, platform: null })}>
            <i className="ri-add-line mr-1" />
            {tp("add")}
          </button>
        </>
      }
    >
      <ErrorBox message={error} />
      {notice && <Notice tone="ok">{notice}</Notice>}
      <PlatformKindSummary
        platforms={kindScope}
        report={kindScopeSpend}
        rates={rates}
        today={today}
        value={filters.kind}
        onChange={(kind) => updateFilters({ ...filters, kind })}
      />
      <PlatformFiltersBar
        value={filters}
        onChange={updateFilters}
        clients={clients}
        categories={categories}
        currentMonth={currentMonth()}
        shown={visible.length}
        total={platforms.length}
      />
      <PlatformSpendCharts report={visibleSpend} today={billing?.today || currentDay()} />
      {spikes.map((s) => (
        <Notice key={`${s.name}-${s.period}`} tone="warn">
          <i className="ri-line-chart-line mr-1" />
          {tp("consumption.spike", { name: s.name, period: monthLabel(s.period, language), change: percentChange(s.change, language), average: money(s.average3, "MXN", language) })}
        </Notice>
      ))}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={tp("kpi.active")} value={String(overview.activeCount)} hint={overview.withoutCost ? tp("kpi.withoutCost", { count: overview.withoutCost }) : undefined} />
        <Kpi label={tp("kpi.monthly")} value={currencyTotals(overview.monthlyByCurrency, language)} hint={monthlyHint} />
        <Kpi label={tp("kpi.seats")} value={String(overview.seatsTotal)} hint={tp("kpi.seatsAssigned", { count: overview.seatsAssigned })} />
        <Kpi
          label={tp("kpi.missingInvoices")}
          value={String(missingInvoices)}
          hint={billing?.pendingInvoices ? `${tp("kpi.pending")}: ${billing.pendingInvoices}` : tp("kpi.missingInvoicesHint")}
          tone={missingInvoices ? "bad" : "good"}
        />
      </div>

      <div className={c.card}>
        <p className={`mb-2 text-xs ${c.muted}`}>{tp("kpi.byModel")}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="by-model">
          {PLATFORM_BILLING_MODELS.map((m) => (
            <div key={m} className={`rounded-xl border p-3 ${c.border}`}>
              <div className="flex items-center justify-between">
                <Chip className={MODEL_TONES[m]}>{tp(`model.${m}`)}</Chip>
                <span className={`text-sm font-semibold ${c.text}`}>{overview.byModel[m].count}</span>
              </div>
              <p className={`mt-2 text-lg font-bold tabular-nums ${c.text}`}>{currencyTotals(overview.byModel[m].monthlyByCurrency, language)}</p>
              {m === "asiento" && <p className={`text-xs ${c.muted}`}>{`${tp("col.seats")}: ${overview.byModel.asiento.seats}`}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className={`${c.card} overflow-x-auto p-0`}>
        <table className="w-full min-w-[1100px] text-sm" aria-label={tp("title")}>
          <thead>
            <tr className={`text-left text-xs ${c.muted}`}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 ${col.numeric ? "text-right" : ""}`}
                  aria-sort={sort.key === col.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => toggleSort(col.key)}>
                    {tp(`col.${col.key}`)}
                    {sort.key === col.key && <i className={sort.dir === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />}
                  </button>
                </th>
              ))}
              <th className="px-3 py-2">{tp("col.gaps")}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p = platforms.find((x) => x.id === r.id) as Platform;
              const gaps = gapsById.get(r.id);
              return (
                <tr key={r.id} className={`border-t ${c.border} ${c.text} ${r.active ? "" : "opacity-50"}`} data-testid={`row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">
                        {r.name}
                      </a>
                    ) : (
                      r.name
                    )}
                    <Chip className={`ml-2 ${PLATFORM_KIND_TONES[platformKindOf(p)]}`}>{tp(`kind.${platformKindOf(p)}`)}</Chip>
                    {!r.active && <Chip className="ml-2 bg-gray-400/15 text-gray-500">{tp("inactive")}</Chip>}
                    <DiligenceLine p={p} />
                  </td>
                  <td className={`px-3 py-2 ${c.muted}`}>{r.vendor || "—"}</td>
                  <td className="px-3 py-2">
                    <Chip className={MODEL_TONES[r.model]}>{tp(`model.${r.model}`)}</Chip>
                    {r.unit && <span className={`ml-1 text-xs ${c.muted}`}>{r.unit}</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.seats ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{r.unitPrice != null ? money(r.unitPrice, r.currency, language) : "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums">{r.monthlyCost != null ? money(r.monthlyCost, r.currency, language) : "—"}</td>
                  <td className="px-3 py-2">{t(`expenses.frequency.${r.frequency}`)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{r.nextRenewal || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="whitespace-nowrap">{r.card ? (r.card.startsWith("•") ? r.card : t(`expenses.payment.${r.card}`)) : "—"}</span>
                    {p.paymentNotes && <p className={`mt-0.5 max-w-[220px] text-xs ${c.muted}`}>{p.paymentNotes}</p>}
                  </td>
                  <td className="px-3 py-2">{r.owner || "—"}</td>
                  <td className="px-3 py-2">
                    {gaps && gaps.missingInvoices > 0 && <Chip className={GAP_TONES.falta_factura}>{`${tp("gap.falta_factura")}: ${gaps.missingInvoices}`}</Chip>}
                    {gaps && gaps.missingCharges > 0 && <Chip className={`ml-1 ${GAP_TONES.falta_cargo}`}>{`${tp("gap.falta_cargo")}: ${gaps.missingCharges}`}</Chip>}
                    {gaps && !gaps.missingInvoices && !gaps.missingCharges && <span className={c.muted}>—</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <button type="button" aria-label={`invoices-${r.id}`} title={tp("invoices.history")} className="mr-3 text-sky-600" onClick={() => setHistory(p)}>
                      <i className="ri-file-list-3-line" />
                    </button>
                    <button type="button" aria-label={`edit-${r.id}`} className="mr-3 text-teal-600" onClick={() => setEditing({ open: true, platform: p })}>
                      <i className="ri-edit-line" />
                    </button>
                    <button type="button" aria-label={`delete-${r.id}`} className="text-red-500" onClick={() => remove(p)}>
                      <i className="ri-delete-bin-line" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 2} className={`px-4 py-8 text-center ${c.muted}`}>
                  {platforms.length > 0 ? tp("filters.noResults") : tp("empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {visible.length > 0 && (
        <div className={`${c.card} overflow-x-auto p-0`}>
          <h2 className={`px-4 pt-3 text-sm font-semibold ${c.text}`}>{tp("paid.title")}</h2>
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className={`text-left text-xs ${c.muted}`}>
                <th className="px-4 py-2">{tp("col.name")}</th>
                <th className="px-2 py-2 text-right">{tp("col.monthlyCost")}</th>
                {months.map((m) => (
                  <th key={m} className="px-2 py-2 text-right">
                    {monthLabel(m, language)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const r = visibleReport.find((x) => x.platformId === p.id);
                const g = gapsById.get(p.id);
                return (
                  <tr key={p.id} className={`border-t ${c.border} ${c.text} ${p.active === false ? "opacity-50" : ""}`}>
                    <td className="px-4 py-2">{p.name}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{r?.expectedMonthly != null ? money(r.expectedMonthly, p.currency, language) : "—"}</td>
                    {(r?.months || []).map((m) => {
                      const gap = g?.months.find((x) => x.period === m.period);
                      const title = m.missing ? tp("paid.noCharge") : gap?.status === "falta_factura" ? tp("paid.missingInvoice") : gap?.status === "falta_cargo" ? tp("paid.missingCharge") : undefined;
                      return (
                        <td key={m.period} className={`whitespace-nowrap px-2 py-2 text-right tabular-nums ${m.missing ? "text-amber-600" : ""}`} title={title}>
                          {m.paid ? money(m.paid, "MXN", language) : m.missing ? "⚠" : "—"}
                          {gap?.status === "falta_factura" && <i className="ri-file-warning-line ml-1 text-amber-600" aria-label={tp("paid.missingInvoice")} />}
                          {gap?.status === "falta_cargo" && <i className="ri-bank-card-line ml-1 text-sky-600" aria-label={tp("paid.missingCharge")} />}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className={`${c.card} overflow-x-auto`}>
        <h2 className={`text-sm font-semibold ${c.text}`}>{tp("consumption.title")}</h2>
        <p className={`mb-3 text-xs ${c.muted}`}>{tp("consumption.subtitle")}</p>
        {consumption.length === 0 && <p className={`text-sm ${c.muted}`}>{tp("consumption.empty")}</p>}
        {consumption.map((r) => (
          <div key={r.platformId} className="mb-4" data-testid={`consumption-${r.platformId}`}>
            <p className={`text-sm font-medium ${c.text}`}>
              {r.name}
              {r.unit && <span className={`ml-1 text-xs ${c.muted}`}>({r.unit})</span>}
              <span className={`ml-2 text-xs ${c.muted}`}>{tp("consumption.threshold", { value: percentChange(r.threshold, language) })}</span>
            </p>
            <div className="mt-1 flex gap-1 overflow-x-auto">
              {r.months.map((m) => (
                <div
                  key={m.period}
                  className={`min-w-[84px] rounded-lg border px-2 py-1 text-right text-xs ${m.spike ? "border-amber-500 bg-amber-500/10" : c.border}`}
                  title={m.average3 != null ? `${tp("consumption.average")}: ${money(m.average3, "MXN", language)}` : undefined}
                >
                  <p className={c.muted}>{monthLabel(m.period, language)}</p>
                  <p className={`font-medium tabular-nums ${c.text}`}>{money(m.paid, "MXN", language)}</p>
                  {m.change != null && <p className={m.spike ? "text-amber-600" : c.muted}>{percentChange(m.change, language)}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <PlatformDialog
        open={editing.open}
        platform={editing.platform}
        onClose={() => setEditing({ open: false, platform: null })}
        onSaved={async (saved) => {
          setEditing({ open: false, platform: null });
          setNotice(
            saved.linkedExpenses
              ? tp("linked", { count: saved.linkedExpenses })
              : saved.recategorizedExpenses
                ? tp("kind.recategorized", { count: saved.recategorizedExpenses })
                : null,
          );
          await load();
        }}
      />
      <PlatformInvoicesDialog open={invoicesOpen} platforms={platforms} onClose={() => setInvoicesOpen(false)} onChanged={load} />
      <PlatformInvoiceHistory platform={history} gaps={history ? gapsById.get(history.id) : undefined} onClose={() => setHistory(null)} onChanged={load} />
    </Page>
  );
}
